/**
 * Income Statement Form Component
 * Displays income statement data entry table for multiple years
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle size={14} className="inline text-blue-600 cursor-help" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg bottom-full left-1/2 -translate-x-1/4 mb-1 pointer-events-none">
      {text}
    </div>
  </div>
);

HelpTooltip.propTypes = {
  text: PropTypes.string.isRequired
};

const INCOME_STATEMENT_FIELDS = [
  {
    label: 'Revenue/Income',
    field: 'revenue',
    help: 'Total sales or income generated by the business.'
  },
  {
    label: 'Cost of Goods Sold (COGS)',
    field: 'cogs',
    help: 'Direct costs attributable to production of goods sold: materials, direct labor, manufacturing overhead.'
  },
  {
    label: 'Gross Profit',
    field: 'gross_profit',
    help: 'Auto-calculated: Revenue - COGS. Shows profitability before operating expenses.',
    readonly: true
  },
  {
    label: 'Operating Expenses (OPEX)',
    field: 'operating_expenses',
    help: 'General operating expenses including salaries, rent, utilities, marketing, admin costs (excluding COGS, D&A, and interest).'
  },
  {
    label: 'EBITDA',
    field: 'ebitda',
    help: 'Auto-calculated: Gross Profit - Operating Expenses. Earnings Before Interest, Tax, Depreciation, and Amortization.',
    readonly: true
  },
  {
    label: 'Depreciation/Amortization (D&A)',
    field: 'depreciation',
    help: 'Non-cash expenses for asset depreciation and intangible asset amortization.'
  },
  {
    label: 'EBIT',
    field: 'ebit',
    help: 'Auto-calculated: EBITDA - Depreciation/Amortization. Earnings Before Interest and Tax (operating profit).',
    readonly: true
  },
  {
    label: 'Interest Expense',
    field: 'interest_expense',
    help: 'Interest paid on debt obligations. Enter the actual interest expense for the period.'
  },
  {
    label: 'Tax Expense',
    field: 'tax_expense',
    help: 'Income tax expense for the period. Enter actual taxes paid or accrued.'
  },
  {
    label: 'Net Income',
    field: 'profit_after_tax',
    help: 'Auto-calculated: EBIT - Interest Expense - Tax Expense. Net income after all expenses and taxes.',
    readonly: true
  },
  {
    label: 'Add-backs Total',
    field: 'addbacks_total',
    help: 'Auto-calculated sum of all add-backs from the Add-backs tab.',
    readonly: true
  },
  {
    label: "Seller's Discretionary Earnings (SDE)",
    field: 'sde',
    help: 'Auto-calculated as EBITDA + Add-backs. Represents true owner earnings available to a new buyer.',
    readonly: true
  }
];

const IncomeStatementForm = ({
  years,
  financialData,
  formatNumberWithCommas,
  handleInputChange
}) => {
  return (
    <div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left p-3 border">Line Item</th>
            {years.map(y => <th key={y} className="p-3 border">{y}</th>)}
          </tr>
        </thead>
        <tbody>
          {INCOME_STATEMENT_FIELDS.map((item, idx) => (
            <tr key={item.field} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="p-3 border font-medium">
                {item.label}
                {item.help && <HelpTooltip text={item.help} />}
                {item.readonly && <span className="ml-2 text-xs text-green-600">(Auto)</span>}
              </td>
              {years.map(year => (
                <td key={year} className="p-2 border">
                  <input
                    type="text"
                    value={formatNumberWithCommas(financialData[item.field][year])}
                    onChange={(e) => handleInputChange(item.field, year, e.target.value)}
                    className={`w-full px-2 py-1 border rounded ${
                      item.readonly ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="$0"
                    readOnly={item.readonly}
                    disabled={item.readonly}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

IncomeStatementForm.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired
};

export default IncomeStatementForm;
